/**
 * Frontend auth-state. Henter den aktuelle session-bruger fra /api/auth/me og
 * eksponerer login-/logout-helpers. Ingen Pinia (D-010) — reactive object.
 */

import { reactive } from 'vue';

import { fetchPermissions } from './usePermissions';

export interface AuthUser {
  id: number;
  email: string;
  displayName: string | null;
  roles: string[];
  /** Profilbillede (data-URL fra Microsoft Graph) eller null. */
  avatarUrl?: string | null;
  /**
   * [D-141]: brugerens side-grants (route-path → niveau). INGEN nøgle =
   * INGEN adgang (fail-closed). super_admin bypasser og behøver ingen —
   * canAccessPath/canEditPath (usePermissions.ts) tjekker rollen først.
   */
  grants: Record<string, 'view' | 'edit'>;
}

interface AuthState {
  user: AuthUser | null;
  loaded: boolean;
}

export const auth = reactive<AuthState>({ user: null, loaded: false });

/** Henter nuværende session-bruger. Sætter user=null hvis ikke logget ind. */
export async function fetchMe(): Promise<void> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
    const user = res.ok ? ((await res.json()) as { user: AuthUser }).user : null;
    // Defensivt: grants skal altid være et objekt (ældre server-svar uden
    // feltet må ikke give runtime-fejl i adgangstjekkene).
    if (user && !user.grants) user.grants = {};
    auth.user = user;
  } catch {
    auth.user = null;
  } finally {
    auth.loaded = true;
  }
}

/**
 * Idempotent session-hydrering (F5): henter bruger + permissions ÉN gang og
 * cacher løftet. Router-guarden afventer dette FREM FOR at main.ts blokerer
 * first paint på de to API-kald — app-shellen kan males med det samme, og kun
 * det routede indhold venter på hydreringen. Efterfølgende kald (hver
 * navigation) returnerer det allerede-resolvede løfte og er dermed gratis.
 */
let sessionReady: Promise<void> | null = null;
export function ensureSessionLoaded(): Promise<void> {
  if (!sessionReady) {
    sessionReady = Promise.all([fetchMe(), fetchPermissions()]).then(() => undefined);
  }
  return sessionReady;
}

/** Starter Microsoft-login (fuld redirect til serveren → Entra). */
export function loginWithMicrosoft(): void {
  window.location.href = '/api/auth/microsoft/login';
}

/** Starter Google-login ([D-143]; knappen vises kun når serveren har flowet slået til). */
export function loginWithGoogle(): void {
  window.location.href = '/api/auth/google/login';
}

/** Logger ud og sender til login-siden med FULD reload — ikke SPA-navigation.
 *  Hash-navigationen alene efterlod den indlæste side (DOM + data i
 *  hukommelsen) synlig efter logout (Charlies fund 06-08-2026); på en delt
 *  maskine kunne næste person se seneste skærmbillede. Reload river alt ned;
 *  serversessionen er allerede destrueret, så back/bfcache lander fail-closed
 *  på login-guarden. */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } catch {
    /* ignorér — vi rydder lokalt uanset hvad */
  }
  auth.user = null;
  window.location.replace('/#/login');
  window.location.reload();
}
